/**
 * Unified Time Formatting Utilities
 * Eliminates duplicated time formatting logic across 8+ shift management components
 * Provides consistent date/time displays throughout the application
 */

import { format, differenceInMinutes, parseISO, isValid } from 'date-fns';

// Standardized time formats
export const TIME_FORMATS = {
  TIME_12H: 'h:mm a',           // 2:30 PM
  TIME_24H: 'HH:mm',            // 14:30
  DATE_SHORT: 'MMM dd, yyyy',   // Jan 15, 2024
  DATE_LONG: 'EEEE, MMMM d, yyyy', // Monday, January 15, 2024
  DATETIME_SHORT: 'MMM dd • h:mm a', // Jan 15 • 2:30 PM
  DATETIME_FULL: 'MMM dd, yyyy • h:mm a', // Jan 15, 2024 • 2:30 PM
  TIMESTAMP: 'MMM dd, yyyy • h:mm a', // Jan 15, 2024 • 2:30 PM
} as const;

// Input types - handles both strings and Date objects
type TimeInput = string | Date;

/**
 * Safely parse time input to Date object
 */
const parseTimeInput = (input: TimeInput): Date | null => {
  if (!input) return null;
  
  if (input instanceof Date) {
    return isValid(input) ? input : null;
  }
  
  if (typeof input === 'string') {
    const parsed = parseISO(input);
    return isValid(parsed) ? parsed : null;
  }
  
  return null;
};

/**
 * Format shift time range in standard format
 * Eliminates duplication across ShiftRequestsTab, AvailableShiftsTab, etc.
 * 
 * @param startTime - Start time (string or Date)
 * @param endTime - End time (string or Date)
 * @param options - Formatting options
 * @returns Formatted time range string
 */
export const formatShiftTimeRange = (
  startTime: TimeInput,
  endTime: TimeInput,
  options: {
    includeDate?: boolean;
    use24Hour?: boolean;
    showDuration?: boolean;
    compact?: boolean;
  } = {}
): string => {
  const start = parseTimeInput(startTime);
  const end = parseTimeInput(endTime);
  
  if (!start || !end) {
    return 'Invalid time range';
  }
  
  const { includeDate = true, use24Hour = false, showDuration = false, compact = false } = options;
  
  // Choose time format
  const timeFormat = use24Hour ? TIME_FORMATS.TIME_24H : TIME_FORMATS.TIME_12H;
  
  // Format components
  const dateStr = includeDate ? format(start, TIME_FORMATS.DATE_SHORT) : '';
  const startTimeStr = format(start, timeFormat);
  const endTimeStr = format(end, timeFormat);
  
  // Build time range
  const timeRange = compact 
    ? `${startTimeStr}-${endTimeStr}`
    : `${startTimeStr} - ${endTimeStr}`;
  
  // Add duration if requested
  const duration = showDuration 
    ? ` (${formatDuration(differenceInMinutes(end, start))})`
    : '';
  
  // Combine components
  if (includeDate && dateStr) {
    return compact 
      ? `${dateStr} • ${timeRange}${duration}`
      : `${dateStr} • ${timeRange}${duration}`;
  }
  
  return `${timeRange}${duration}`;
};

/**
 * Format single timestamp with consistent patterns
 * Used for request timestamps, last updated times, etc.
 */
export const formatTimestamp = (
  timestamp: TimeInput,
  options: {
    includeTime?: boolean;
    relative?: boolean;
    compact?: boolean;
  } = {}
): string => {
  const date = parseTimeInput(timestamp);
  
  if (!date) {
    return 'Invalid date';
  }
  
  const { includeTime = true, compact = false } = options;
  
  if (includeTime) {
    return format(date, compact ? TIME_FORMATS.DATETIME_SHORT : TIME_FORMATS.DATETIME_FULL);
  } else {
    return format(date, TIME_FORMATS.DATE_SHORT);
  }
};

/**
 * Format duration in a human-readable format
 * Handles various duration representations consistently
 */
export const formatDuration = (minutes: number): string => {
  if (minutes < 0) {
    return `${formatDuration(-minutes)} ago`;
  }
  
  if (minutes === 0) {
    return '0m';
  }
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) {
    return `${mins}m`;
  }
  
  if (mins === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${mins}m`;
};

/**
 * Format time with consistent 12/24 hour handling
 */
export const formatTime = (
  time: TimeInput, 
  use24Hour: boolean = false
): string => {
  const date = parseTimeInput(time);
  
  if (!date) {
    return 'Invalid time';
  }
  
  return format(date, use24Hour ? TIME_FORMATS.TIME_24H : TIME_FORMATS.TIME_12H);
};

/**
 * Format date with consistent patterns
 */
export const formatDate = (
  date: TimeInput,
  long: boolean = false
): string => {
  const parsedDate = parseTimeInput(date);
  
  if (!parsedDate) {
    return 'Invalid date';
  }
  
  return format(parsedDate, long ? TIME_FORMATS.DATE_LONG : TIME_FORMATS.DATE_SHORT);
};

/**
 * Create request timeline text
 * Used in both ShiftRequestsTab and AvailableShiftsTab
 */
export const formatRequestTimeline = (
  requestedAt: TimeInput,
  reviewedAt?: TimeInput | null
): string => {
  const requested = formatTimestamp(requestedAt, { compact: true });
  
  if (reviewedAt) {
    const reviewed = formatTimestamp(reviewedAt, { compact: true });
    return `Requested ${requested} • Reviewed ${reviewed}`;
  }
  
  return `Requested ${requested}`;
};

/**
 * Format shift time for compact display in lists/cards
 * Used in BulkShiftOperations and other list views
 */
export const formatShiftTimeCompact = (
  startTime: TimeInput,
  use24Hour: boolean = true
): string => {
  const start = parseTimeInput(startTime);
  
  if (!start) {
    return 'Invalid time';
  }
  
  const timeFormat = use24Hour ? TIME_FORMATS.TIME_24H : TIME_FORMATS.TIME_12H;
  return format(start, timeFormat);
};

/**
 * Utility to check if two time ranges are on the same day
 */
export const isSameDay = (date1: TimeInput, date2: TimeInput): boolean => {
  const d1 = parseTimeInput(date1);
  const d2 = parseTimeInput(date2);
  
  if (!d1 || !d2) return false;
  
  return format(d1, 'yyyy-MM-dd') === format(d2, 'yyyy-MM-dd');
};

/**
 * Get display time for shift based on business context
 * Handles timezone considerations and business rules
 */
export const getShiftDisplayTime = (
  startTime: TimeInput,
  endTime: TimeInput,
  businessConfig?: {
    use24Hour?: boolean;
    timezone?: string;
    showDuration?: boolean;
  }
): {
  timeRange: string;
  duration: string;
  isOvernight: boolean;
} => {
  const start = parseTimeInput(startTime);
  const end = parseTimeInput(endTime);
  
  if (!start || !end) {
    return {
      timeRange: 'Invalid time range',
      duration: '0m',
      isOvernight: false
    };
  }
  
  const use24Hour = businessConfig?.use24Hour ?? false;
  const showDuration = businessConfig?.showDuration ?? true;
  
  const isOvernight = !isSameDay(start, end);
  const duration = formatDuration(differenceInMinutes(end, start));
  
  const timeRange = formatShiftTimeRange(start, end, {
    includeDate: isOvernight,
    use24Hour,
    showDuration: false, // Handle duration separately
    compact: true
  });
  
  return {
    timeRange,
    duration,
    isOvernight
  };
};

// Export commonly used formats for direct access
export const COMMON_FORMATS = {
  formatShiftTimeRange,
  formatTimestamp,
  formatDuration,
  formatTime,
  formatDate,
  formatRequestTimeline,
  formatShiftTimeCompact,
} as const;

// TIME_FORMATS is already exported above, no need to re-export